import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { ConfigResponse } from './feature-flag.interface';
import { LocalStorageService } from '../../../../core/services/local-storage/local-storage.service';
import { STORAGE_AUTH_TOKEN } from '../../../../core/constants/craig.constants';
import { StreamingConfigService } from '../config/streaming-config.service';

@Injectable({
    providedIn: 'root'
})
export class FeatureFlagService {
    private featureFlags = new BehaviorSubject<{ [key: string]: boolean }>({});
    private readonly OVERRIDE_PREFIX = 'feature_flag_override_';

    public featureFlags$ = this.featureFlags.asObservable();

    constructor(
        private http: HttpClient,
        private localStorageService: LocalStorageService,
        private streamingConfigService: StreamingConfigService
    ) {}

    /**
     * Load feature flags from the config endpoint with authentication
     */
    loadFeatureFlags(): Observable<{ [key: string]: boolean }> {
        const headers = this.getAuthHeaders();

        if (!headers) {
            console.error('🚨 No authentication token available for feature flags');
            const emptyFlags = {};
            this.featureFlags.next(emptyFlags);
            return of(emptyFlags);
        }

        const configUrl = '/api/config';
        return this.http.get<ConfigResponse>(configUrl, { headers }).pipe(
            map((response) => {
                // console.log('🔍 Raw API response:', response);

                const rawFlags = response.feature_flags || {};
                // console.log('🔍 Raw feature flags:', rawFlags);

                // Convert nested structure to simple boolean flags
                const flags: { [key: string]: boolean } = {};

                Object.keys(rawFlags).forEach((key) => {
                    const flagConfig = rawFlags[key];
                    const isEnabled = flagConfig?.enabled || false;
                    flags[key] = isEnabled;
                    // console.log(`🔍 Processing flag ${key}:`, flagConfig, '→', isEnabled);
                });

                this.featureFlags.next(flags);
                // console.log('🔐 Feature flags processed:', flags);
                // console.log('🚀 streamingChat enabled:', flags['streamingChat']);
                return flags;
            }),
            catchError((error) => {
                console.error('🚨 Failed to load feature flags:', error);
                // Return empty flags on error
                const emptyFlags = {};
                this.featureFlags.next(emptyFlags);
                return of(emptyFlags);
            })
        );
    }

    /**
     * Get a specific feature flag value
     * First checks localStorage for override, then falls back to API value
     */
    getFeatureFlag(flagName: string): boolean {
        // Check for local override first
        const override = this.getLocalOverride(flagName);
        if (override !== null) {
            // Using local override
            return override;
        }

        // Fall back to API value
        const currentFlags = this.featureFlags.value;
        return currentFlags[flagName] || false;
    }

    /**
     * Check if streaming chat feature is enabled
     */
    isStreamingChatEnabled(): boolean {
        return this.getFeatureFlag('streamingChat');
    }

    /**
     * Get current feature flags as observable
     */
    getFeatureFlags(): Observable<{ [key: string]: boolean }> {
        return this.featureFlags$;
    }

    /**
     * Refresh feature flags (useful if auth token changes)
     */
    refreshFeatureFlags(): void {
        this.loadFeatureFlags().subscribe({
            next: (flags) => console.log('🔄 Feature flags refreshed:', Object.keys(flags)),
            error: (error) => console.error('❌ Failed to refresh feature flags:', error)
        });
    }

    /**
     * Get authentication headers for API requests
     */
    private getAuthHeaders(): HttpHeaders | null {
        const authToken = this.localStorageService.getRawItem(STORAGE_AUTH_TOKEN);

        if (!authToken) {
            return null;
        }

        return new HttpHeaders({
            Authorization: authToken,
            'Content-Type': 'application/json'
        });
    }

    /**
     * Get local override for a feature flag from localStorage
     * Returns null if no override is set
     */
    private getLocalOverride(flagName: string): boolean | null {
        try {
            const overrideKey = `${this.OVERRIDE_PREFIX}${flagName}`;
            const overrideValue = localStorage.getItem(overrideKey);

            if (overrideValue === null) {
                return null;
            }

            // Parse the stored value (handle 'true'/'false' strings)
            return overrideValue === 'true';
        } catch (error) {
            console.error(`Failed to get local override for ${flagName}:`, error);
            return null;
        }
    }

    /**
     * Set a local override for a feature flag
     * Use this in the browser console for development/troubleshooting
     */
    setLocalOverride(flagName: string, value: boolean): void {
        try {
            const overrideKey = `${this.OVERRIDE_PREFIX}${flagName}`;
            localStorage.setItem(overrideKey, value.toString());
            // console.log(`✅ Local override set: ${flagName} = ${value}`);
            // console.log(`   To remove: featureFlagService.removeLocalOverride('${flagName}')`);
        } catch (error) {
            console.error(`Failed to set local override for ${flagName}:`, error);
        }
    }

    /**
     * Remove a local override for a feature flag
     */
    removeLocalOverride(flagName: string): void {
        try {
            const overrideKey = `${this.OVERRIDE_PREFIX}${flagName}`;
            localStorage.removeItem(overrideKey);
            // console.log(`🗑️ Local override removed for ${flagName}`);
        } catch (error) {
            console.error(`Failed to remove local override for ${flagName}:`, error);
        }
    }

    /**
     * Clear all local feature flag overrides
     */
    clearAllLocalOverrides(): void {
        try {
            const keysToRemove: string[] = [];

            // Find all override keys
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key && key.startsWith(this.OVERRIDE_PREFIX)) {
                    keysToRemove.push(key);
                }
            }

            // Remove all override keys
            keysToRemove.forEach((key) => localStorage.removeItem(key));

            // console.log(`🗑️ Cleared ${keysToRemove.length} local override(s)`);
        } catch (error) {
            console.error('Failed to clear local overrides:', error);
        }
    }

    /**
     * Get all current local overrides
     * Useful for debugging
     */
    getAllLocalOverrides(): { [key: string]: boolean } {
        const overrides: { [key: string]: boolean } = {};

        try {
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key && key.startsWith(this.OVERRIDE_PREFIX)) {
                    const flagName = key.replace(this.OVERRIDE_PREFIX, '');
                    const value = localStorage.getItem(key);
                    if (value !== null) {
                        overrides[flagName] = value === 'true';
                    }
                }
            }
        } catch (error) {
            console.error('Failed to get local overrides:', error);
        }

        return overrides;
    }

    /**
     * Quick debug to check flag status
     */
    checkFlag(flagName: string): void {
        const apiValue = this.featureFlags.value[flagName];
        const overrideValue = this.getLocalOverride(flagName);
        const effectiveValue = this.getFeatureFlag(flagName);

        // console.log(`🏁 ${flagName}:`);
        // console.log(`   API value: ${apiValue || false}`);
        // console.log(`   Override: ${overrideValue !== null ? overrideValue : 'none'}`);
        // console.log(`   Effective: ${effectiveValue}`);

        if (overrideValue === null) {
            // console.log(`   💡 To override: ff.setLocalOverride('${flagName}', true/false)`);
        } else {
            // console.log(`   💡 To remove override: ff.removeLocalOverride('${flagName}')`);
        }
    }
}
